import React, { useState, useEffect, useRef, useContext } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../components/Layout';
import { TextField, Button, Checkbox, Autocomplete, FormControlLabel, Container, Grid, Box, Alert, Snackbar, Tooltip, Typography, Card, CardContent, Stack, Accordion, AccordionSummary, AccordionDetails, Chip, Dialog, DialogActions, DialogContent, DialogTitle, AlertTitle } from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import { useTheme, useMediaQuery } from '@mui/material';
import { AccessTime, CalendarMonth, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const PinsGrid = () => {
    const [rows, setRows] = useState([]);
    const [gridHeight, setGridHeight] = useState(400);  // default value
    const [loadingAddOrUpdate, setLoadingAddOrUpdate] = useState(false);
    const [deletingIds, setDeletingIds] = useState(new Set());
    const [gridLoading, setGridLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { globalTenantId } = useContext(AuthContext);
    const [sharePINs, setSharePINs] = useState(false);
    const [sharingMasterTenantID, setSharingMasterTenantID] = useState(null);
    const [sharingMasterTenantName, setSharingMasterTenantName] = useState('');
    const [responderAliases, setResponderAliases] = useState([]);
    const [showAliases, setShowAliases] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [enableDateRestriction, setEnableDateRestriction] = useState(false);
    const [enableTimeRestriction, setEnableTimeRestriction] = useState(false);
    const [restrictionsExpanded, setRestrictionsExpanded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pinsEnabled, setPinsEnabled] = useState(null);
    const [showAccessWarning, setShowAccessWarning] = useState(false);
    const [accessWarningMessage, setAccessWarningMessage] = useState('');

    useEffect(() => {
        if (!globalTenantId) return; 
        fetchData();
        fetchResponderAliases(); 
        fetchPINStatus();
    }, [globalTenantId]);
    
    useEffect(() => {
        const viewportHeight = window.innerHeight;
        const calculatedHeight = viewportHeight - 300; // Simplified calculation
        setGridHeight(calculatedHeight);
    }, []); 
    
    // State for form inputs
    const [form, setForm] = useState({
        pin: '', 
        name: '', 
        internalName: '',
        responderAliasId: null,
        startDate: '',
        endDate: '',
        timeOfDayStart: '',
        timeOfDayEnd: ''
    });
    
    const fetchResponderAliases = () => {

        axios.get(`/api/responder-aliases-isenabled`,  { params: { tenantId: sharingMasterTenantID || globalTenantId } })
        .then(response => {
            if (response.data.SettingAllowAliasesForPINs === true) {
                console.log('true')
                setShowAliases(true);
            }
            else {
                setShowAliases(false);
            }
        })

        axios.get('/api/responder-aliases-kiosk',  { params: { tenantId: sharingMasterTenantID || globalTenantId } })
        .then(response => {
            setResponderAliases(response.data);
        
        })
        .catch(error => console.error('Error fetching responder aliases:', error));
    };

    const fetchPINStatus = () => {
        axios.get('/api/tenants-pins-status', { 
            params: { tenantId: globalTenantId } // Always use global tenant ID for status
        })
        .then(response => {
            setPinsEnabled(response.data.SettingIncludePINSButton);
        })
        .catch(error => console.error('Error fetching PIN status:', error));
    };

    // Function to handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prevForm => ({
            ...prevForm,
            [name]: value,
        }));
    };

    // Add prepareSubmitData function
    const prepareSubmitData = (formData) => {
        return {
            ...formData,
            timeOfDayStart: enableTimeRestriction ? formData.timeOfDayStart : null,
            timeOfDayEnd: enableTimeRestriction ? formData.timeOfDayEnd : null,
            startDate: enableDateRestriction ? (formData.startDate || null) : null,
            endDate: enableDateRestriction ? (formData.endDate || null) : null
        };
    };

    // Function to handle form submission
    const handleAddSubmit = (e) => {
        e.preventDefault();
        if (!form.pin.trim()) {
            setErrorMessage('PIN is required');
            setShowAlert(true);
            return;
        }
        if (!form.name.trim()) {
            setErrorMessage('Welcome Name is required');
            setShowAlert(true);
            return;
        }

        // Validate date restrictions
        if (enableDateRestriction && !form.startDate && !form.endDate) {
            setErrorMessage('When date restriction is enabled, you must specify at least a start date or end date');
            setShowAlert(true);
            return;
        }

        // Validate time restrictions
        if (enableTimeRestriction && (!form.timeOfDayStart || !form.timeOfDayEnd)) {
            setErrorMessage('When time restriction is enabled, both start and end times are required');
            setShowAlert(true);
            return;
        }

        const submitData = prepareSubmitData(form);

        setLoadingAddOrUpdate(true);
        axios.post('/api/pins', submitData, { params: { tenantId: sharingMasterTenantID || globalTenantId } })
            .then(result => {
                setDialogOpen(false);
                setLoadingAddOrUpdate(false);
                resetForm();
                fetchData();
            })
            .catch(error => {
                setLoadingAddOrUpdate(false);
                setErrorMessage(error.response.data);
                setShowAlert(true);
            });
    };

    const fetchData = () => {
        setGridLoading(true);
        setShowAccessWarning(false); // Reset warning state

        // get the share PINs setting
        axios.get('/api/pins/is-shared', { params: { tenantId: globalTenantId } })
        .then(result => {
            let shareId = null;
            setSharePINs(result.data.SharePINs);
            if (result.data.SharePINs) {
                shareId = result.data.SharingPINsMasterTenantID; 
                setSharingMasterTenantID(shareId);
                setSharingMasterTenantName(result.data.masterTenantName);
            }
            else {
                setSharingMasterTenantID(null);
            }
            // get the PINs either from the shared tenant or the global one if sharing is not enabled
            axios.get('/api/pins', { params: { tenantId: shareId || globalTenantId } })
            .then(result => {
                let data = result.data;
                setGridLoading(false);
                setRows(data);
            })
            .catch(error => {
                setGridLoading(false);
                if (error.response?.data?.includes('Not allowed to access this tenant')) {
                    setShowAccessWarning(true);
                    setAccessWarningMessage(`This tenant has shared PINs from another tenant to which you don't have access. Please contact your administrator.`);
                    setRows([]); // Clear any existing data
                }
                console.error('Error fetching data:', error);
            });
        })
        .catch(error => {
            setGridLoading(false);
            console.error('Error fetching data:', error);
        });
    };

    // State for editing mode and edited entry
    const [isEditing, setIsEditing] = useState(false);
    const [editedEntry, setEditedEntry] = useState(null);

    // Function to handle editing
    const handleEditClick = (entry) => {
        setIsEditing(true);
        setEditedEntry(entry);
        setDialogOpen(true);
        
        const formatDate = (dateString) => {
            if (!dateString) return '';
            return new Date(dateString).toISOString().split('T')[0];
        };

        const formatTime = (timeString) => {
            if (!timeString) return '';
            return timeString.split('T')[1].substring(0, 5);
        };

        setForm({
            pin: entry.pin,
            name: entry.name,
            internalName: entry.internalName || '',
            responderAliasId: entry.responderAliasId || null,
            startDate: formatDate(entry.startDate),
            endDate: formatDate(entry.endDate),
            timeOfDayStart: formatTime(entry.timeOfDayStart),
            timeOfDayEnd: formatTime(entry.timeOfDayEnd)
        });

        // Set restriction states based on existing data
        setEnableDateRestriction(!!(entry.startDate || entry.endDate));
        setEnableTimeRestriction(!!(entry.timeOfDayStart || entry.timeOfDayEnd));
        setRestrictionsExpanded(!!(entry.startDate || entry.endDate || entry.timeOfDayStart || entry.timeOfDayEnd));
    };

    // Function to handle cancel action
    const handleCancelEdit = () => {
        resetForm();
        setIsEditing(false); // Exit editing mode
        setEditedEntry(null); // Clear edited entry
    };
    
    // Function to handle update submission
    const handleUpdateSubmit = (e) => {
        e.preventDefault();
        if (!form.pin.trim()) {
            setErrorMessage('PIN is required');
            setShowAlert(true);
            return;
        }
        if (!form.name.trim()) {
            setErrorMessage('Welcome Name is required');
            setShowAlert(true);
            return;
        }

        // Validate date restrictions
        if (enableDateRestriction && !form.startDate && !form.endDate) {
            setErrorMessage('When date restriction is enabled, you must specify at least a start date or end date');
            setShowAlert(true);
            return;
        }

        // Validate time restrictions
        if (enableTimeRestriction && (!form.timeOfDayStart || !form.timeOfDayEnd)) {
            setErrorMessage('When time restriction is enabled, both start and end times are required');
            setShowAlert(true);
            return;
        }

        const submitData = prepareSubmitData(form);

        setLoadingAddOrUpdate(true);
        axios.put(`/api/pins/${editedEntry.id}`, submitData, { params: { tenantId: sharingMasterTenantID || globalTenantId } })
            .then(result => {
                setDialogOpen(false);
                setLoadingAddOrUpdate(false);
                resetForm();
                setIsEditing(false);
                setEditedEntry(null);
                fetchData();
            })
            .catch(error => {
                setLoadingAddOrUpdate(false);
                setErrorMessage(error.response.data);
                setShowAlert(true);
            });
    };

    const handleSharePINChange = (event) => {
        setSharePINs(event.target.checked);

        // update the share pins setting
        axios.post(`/api/pins/is-shared?tenantId=${globalTenantId}`, { SharePINs: event.target.checked })
        .then(result=> {
            // do nothing
            fetchData();
        })
        .catch(error => {
            console.error('Error updating share PINs setting:', error);
        });
    };
    
    const handleDelete = (id) => {
        
        setDeletingIds(prevDeletingIds => {
            const newDeletingIds = new Set(prevDeletingIds);
            newDeletingIds.add(id);
            return newDeletingIds;
        });

        axios.delete(`/api/pins/${id}`, { params: { tenantId: sharingMasterTenantID || globalTenantId } })
        .then(result => {
            setDeletingIds(prevDeletingIds => {
                const newDeletingIds = new Set(prevDeletingIds);
                newDeletingIds.delete(id);
                return newDeletingIds;
            });
            // Reset the form and editing mode
            resetForm();
            setIsEditing(false);
            setEditedEntry(null);
            // Refresh the list of pins
            fetchData();
        })
        .catch(error => {
            setDeletingIds(prevDeletingIds => {
                const newDeletingIds = new Set(prevDeletingIds);
                newDeletingIds.delete(id);
                return newDeletingIds;
            });
            console.error('Error deleting pin:', error)
        });
    };

    const resetForm = () => {
        setForm({
            pin: '',
            name: '',
            internalName: '',
            responderAliasId: null,
            startDate: '',
            endDate: '',
            timeOfDayStart: '',
            timeOfDayEnd: ''
        });
        // Reset restriction states
        setEnableDateRestriction(false);
        setEnableTimeRestriction(false);
        setRestrictionsExpanded(false);
    };

    const isPlateActive = (startDate, endDate) => {
        if (!startDate && !endDate) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (startDate && new Date(startDate) > today) return false;
        if (endDate) {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (today > endOfDay) return false;
        }
        return true;
    };

    const getRestrictionChip = (row) => {
        const hasTimeRestriction = row.timeOfDayStart && row.timeOfDayEnd;
        const hasDateRestriction = row.startDate || row.endDate;
        
        if (!hasTimeRestriction && !hasDateRestriction) return null;

        const isInactive = row.startDate || row.endDate ? !isPlateActive(row.startDate, row.endDate) : false;

        let label = [];
        if (hasDateRestriction) label.push('Date');
        if (hasTimeRestriction) label.push('Time');
        
        const formatTime = (timeString) => timeString ? timeString.split('T')[1].substring(0, 5) : '';
        
        let tooltipText = isInactive 
            ? "This PIN is currently inactive due to date restrictions"
            : "This PIN is currently active with the following restrictions:";
        
        if (hasDateRestriction) {
            tooltipText += `\nDates: ${row.startDate ? `from ${new Date(row.startDate).toLocaleDateString()}` : 'no start date'} ${row.endDate ? `until ${new Date(row.endDate).toLocaleDateString()}` : 'no end date'}`;
        }
        if (hasTimeRestriction) {
            tooltipText += `\nTime: ${formatTime(row.timeOfDayStart)} to ${formatTime(row.timeOfDayEnd)} daily`;
        }

        return (
            <Tooltip title={tooltipText}>
                <Chip
                    icon={<AccessTime />}
                    label={`${label.join(' & ')} Restricted${isInactive ? ' (Inactive)' : ''}`}
                    color={isInactive ? "error" : "success"}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                />
            </Tooltip>
        );
    };

    const columns = [
        // 100% of available space

        { field: "pin", headerName: "PIN", flex: 1},
        { field: "name", headerName: "Welcome Name", flex: 1},
        { field: "internalName", headerName: "Internal Name", flex: 1},
        // Include the responderAlias column conditionally
        ...(showAliases ? [{
            field: "responderAlias", 
            headerName: "Alias", 
            flex: 1,
            valueGetter: (params) => {
                const alias = responderAliases.find(alias => alias.id === params.row.responderAliasId);
                return alias ? alias.alias : '';
            }
        }] : []),        
        {
            field: 'restrictions',
            headerName: 'Restrictions',
            width: 260,
            renderCell: (params) => getRestrictionChip(params.row)
        },
        {
            field: "edit",
            headerName: "",
            width: 80,
            renderCell: (params) => (
                <Button onClick={() => handleEditClick(params.row)} variant="contained" color="primary">
                    Edit
                </Button>
            )
        },
        { field: 'delete', headerName: '', width: 80,
        renderCell: (params) => (
            <Button
                onClick={() => handleDelete(params.id)}
                variant="outlined"
                color="secondary"
                disabled={deletingIds.has(params.id)}
            >
                {deletingIds.has(params.id) ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
        ),
        }
    ];

    const handleDialogClose = () => {
        setDialogOpen(false);
        resetForm();
    };

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const filteredRows = rows.filter(row => {
        const searchLower = searchTerm.toLowerCase();
        return (
            row.pin?.toLowerCase().includes(searchLower) ||
            row.name?.toLowerCase().includes(searchLower) ||
            row.internalName?.toLowerCase().includes(searchLower)
        );
    });

    const MobileView = ({ rows }) => (
        <Stack spacing={2}>
            {rows.map(row => (
                <Card key={row.id}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            PIN: {row.pin}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Welcome Name: {row.name}
                        </Typography>
                        {row.internalName && (
                            <Typography variant="body2" gutterBottom>
                                Internal Name: {row.internalName}
                            </Typography>
                        )}
                        {showAliases && row.responderAliasId && (
                            <Typography variant="body2" gutterBottom>
                                Alias: {responderAliases.find(alias => alias.id === row.responderAliasId)?.alias}
                            </Typography>
                        )}
                        {getRestrictionChip(row)}
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={2} 
                            sx={{ mt: 2 }}
                        >
                            <Button 
                                onClick={() => handleEditClick(row)} 
                                variant="contained" 
                                color="primary"
                                fullWidth
                            >
                                Edit
                            </Button>
                            <Button
                                onClick={() => handleDelete(row.id)}
                                variant="outlined"
                                color="secondary"
                                disabled={deletingIds.has(row.id)}
                                fullWidth
                            >
                                {deletingIds.has(row.id) ? <CircularProgress size={24} /> : 'Delete'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );

    return (
        <Layout title='PINs'>
            {pinsEnabled === false && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>PINs are currently disabled</AlertTitle>
                    {sharingMasterTenantID && globalTenantId !== sharingMasterTenantID ? (
                        `PINs are disabled on this kiosk. The PINs shared with ${sharingMasterTenantName} will not be used.`
                    ) : (
                        "PINs are disabled for this kiosk. Only kiosks with PINs enabled can use them."
                    )}
                </Alert>
            )}

            {showAccessWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Access Restricted</AlertTitle>
                    {accessWarningMessage}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                    <Tooltip title={
                        <Typography>
                            When this option is selected, the PINs listed on this screen is the master list. All other devices then use these PINs. 
                            {sharePINs && globalTenantId != sharingMasterTenantID ? 'This option is disabled because this is not the device sharing PINs.' : ''}
                        </Typography>
                    }>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sharePINs}
                                    onChange={handleSharePINChange}
                                    name="sharePINs"
                                    color="primary"
                                    disabled={sharePINs && globalTenantId != sharingMasterTenantID}
                                />
                            }
                            label={'Share PINs across all devices ' + 
                            (sharePINs && globalTenantId != sharingMasterTenantID ? `(PINs are shared with ${sharingMasterTenantName})` : '')}
                        />
                    </Tooltip>                
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button 
                        onClick={() => setDialogOpen(true)} 
                        variant="contained" 
                        color="primary"
                        fullWidth
                        disabled={showAccessWarning}  // Disable button when access warning is shown
                    >
                        Add PIN
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Search PINs, names..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                </Grid>
            </Grid>

            {/* PIN Add/Edit Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleDialogClose}
                maxWidth='md'
                fullWidth
            >
                <form onSubmit={isEditing ? handleUpdateSubmit : handleAddSubmit}>
                    <DialogTitle>
                        {isEditing ? 'Edit PIN' : 'Add PIN'}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2}>
                            {/* Left Column - Basic Information */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="PIN"
                                            name="pin"
                                            value={form.pin}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Welcome Name"
                                            name="name"
                                            value={form.name}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Internal Name"
                                            name="internalName"
                                            value={form.internalName}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    {showAliases && (
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={responderAliases}
                                                getOptionLabel={(option) => option.alias}
                                                value={responderAliases.find(alias => alias.id === form.responderAliasId) || null}
                                                onChange={(event, newValue) => {
                                                    setForm(prevForm => ({
                                                        ...prevForm,
                                                        responderAliasId: newValue ? newValue.id : null
                                                    }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Responder Alias"
                                                        variant="outlined"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Right Column - Restrictions */}
                            <Grid item xs={12} md={6}>
                                <Accordion 
                                    expanded={restrictionsExpanded}
                                    onChange={(e, isExpanded) => setRestrictionsExpanded(isExpanded)}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>Date & Time Access Restrictions</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={enableDateRestriction}
                                                            onChange={(e) => {
                                                                setEnableDateRestriction(e.target.checked);
                                                                if (!e.target.checked) {
                                                                    setForm(prev => ({
                                                                        ...prev,
                                                                        startDate: '',
                                                                        endDate: ''
                                                                    }));
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Enable Date Restriction"
                                                />
                                            </Grid>
                                            
                                            {enableDateRestriction && (
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Tooltip title="Optional. If not set, there is no start date restriction. If set, the PIN will only be active from this date onwards.">
                                                                <TextField
                                                                    fullWidth
                                                                    label="Start Date"
                                                                    type="date"
                                                                    name="startDate"
                                                                    value={form.startDate}
                                                                    onChange={handleInputChange}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Tooltip title="Optional. If not set, there is no end date restriction. If set, the PIN will become inactive after this date.">
                                                                <TextField
                                                                    fullWidth
                                                                    label="End Date"
                                                                    type="date"
                                                                    name="endDate"
                                                                    value={form.endDate}
                                                                    onChange={handleInputChange}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}

                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={enableTimeRestriction}
                                                            onChange={(e) => {
                                                                setEnableTimeRestriction(e.target.checked);
                                                                if (!e.target.checked) {
                                                                    setForm(prev => ({
                                                                        ...prev,
                                                                        timeOfDayStart: '',
                                                                        timeOfDayEnd: ''
                                                                    }));
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Enable Time Restriction"
                                                />
                                            </Grid>

                                            {enableTimeRestriction && (
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Tooltip title="The time of day from which the PIN becomes active. The PIN will not work before this time.">
                                                                <TextField
                                                                    fullWidth
                                                                    label="Start Time"
                                                                    type="time"
                                                                    name="timeOfDayStart"
                                                                    value={form.timeOfDayStart}
                                                                    onChange={handleInputChange}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Tooltip title="The time of day after which the PIN becomes inactive. The PIN will not work after this time.">
                                                                <TextField
                                                                    fullWidth
                                                                    label="End Time"
                                                                    type="time"
                                                                    name="timeOfDayEnd"
                                                                    value={form.timeOfDayEnd}
                                                                    onChange={handleInputChange}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose} color="primary">
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            color="primary" 
                            variant="contained"
                            disabled={loadingAddOrUpdate}
                        >
                            {loadingAddOrUpdate ? 
                                <CircularProgress size={24} /> : 
                                (isEditing ? 'Update' : 'Add')
                            }
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {showAlert && (
                <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={showAlert}
                autoHideDuration={5000}
                message={errorMessage}
                onClose={() => setShowAlert(false)}
                >
                    <Alert severity="error" onClose={() => setShowAlert(false)}>
                    {errorMessage}
                    </Alert>
                </Snackbar>
            )}
            <Box sx={{ mt: 4 }}>
                {isMobile ? (
                    <MobileView rows={filteredRows} />
                ) : (
                    <Box sx={{ height: gridHeight }}>
                        <DataGrid 
                            rows={filteredRows} 
                            columns={columns} 
                            loading={gridLoading}
                            pageSize={5} 
                            components={{
                                Header: {
                                    cell: (params) => <strong>{params.value}</strong>
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Layout>
    );
}

export default PinsGrid;
